import { Link, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import NotificationBell from './NotificationBell';
import PublicActualiteNotifier from './PublicActualiteNotifier';


export default function Header() {
  const { user, role, userRoles, signOut, passwordRequirement } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [animationActive, setAnimationActive] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Détecter le scroll pour l'effet glassmorphism
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lire la date de fin d'animation du bouton « Actualités »
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('system_config')
          .select('config_value')
          .eq('config_key', 'actualites_config')
          .maybeSingle();
        if (cancelled || !data?.config_value) return;
        const end = data.config_value.animation_end;
        setAnimationActive(!end || new Date() < new Date(end));
      } catch {
        // En cas d'échec, on laisse l'animation active par défaut.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fermer le dropdown au clic extérieur
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setUserDropdownOpen(false);
      }
    };
    if (userDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userDropdownOpen]);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  const showPasswordWarning = role === 'patient' &&
    passwordRequirement?.mustChangePassword &&
    !passwordRequirement?.deadlinePassed;

  // Raccourcir le nom pour les écrans moyens
  const getUserInitials = () => {
    return user?.user_metadata?.prenom?.[0] || user?.email?.[0] || '?';
  };

  return (
    <>
    <header
      className={`sticky top-0 z-[100] w-full transition-all duration-500 ${
        scrolled
          ? 'bg-white/85 backdrop-blur-xl shadow-lg border-b border-gray-100/50'
          : 'bg-white shadow-sm'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">

          {/* === LOGO === */}
          <Link to="/" className="flex items-center gap-2 md:gap-2.5 flex-shrink-0 mr-3">
            <img
              src="https://ddfascfiaucteirbzkzd.supabase.co/storage/v1/object/public/assets/brand/logo.jpeg"
              alt="Clinique Sainte Famille"
              className="h-9 w-9 md:h-10 md:w-10 rounded-full object-cover flex-shrink-0"
            />
            {/* Texte: abrégé sur petits écrans, complet sur grands écrans */}
            <span className="hidden sm:inline text-sm md:text-base font-bold text-clinic-blue-dark whitespace-nowrap">
              <span className="xl:hidden">CSF</span>
              <span className="hidden xl:inline">Clinique Sainte Famille</span>
            </span>
          </Link>

          {/* === NAVIGATION PUBLIQUE (Desktop - visible dès md) === */}
          <nav className="hidden md:flex items-center gap-1 lg:gap-3 xl:gap-5 flex-shrink">
            <Link
              to="/"
              className="underline-expand px-2 lg:px-3 py-2 text-xs lg:text-sm font-medium text-clinic-blue-dark hover:text-clinic-blue transition-all duration-300 whitespace-nowrap rounded-lg hover:bg-clinic-blue/5"
            >
              Accueil
            </Link>
            <Link
              to="/services"
              className="underline-expand px-2 lg:px-3 py-2 text-xs lg:text-sm font-medium text-clinic-blue-dark hover:text-clinic-blue transition-all duration-300 whitespace-nowrap rounded-lg hover:bg-clinic-blue/5"
            >
              Nos services
            </Link>
            <Link
              to="/actualites"
              className={
                animationActive
                  ? 'actu-btn relative px-3 lg:px-4 py-1.5 text-xs lg:text-sm whitespace-nowrap'
                  : 'underline-expand px-2 lg:px-3 py-2 text-xs lg:text-sm font-medium text-clinic-blue-dark hover:text-clinic-blue transition-all duration-300 whitespace-nowrap rounded-lg hover:bg-clinic-blue/5'
              }
            >
              <span className="relative z-10 inline-flex items-center gap-1.5">
                <i className="ri-megaphone-line text-sm"></i>
                Actualités
              </span>
            </Link>
            <Link
              to="/contact"
              className="underline-expand px-2 lg:px-3 py-2 text-xs lg:text-sm font-medium text-clinic-blue-dark hover:text-clinic-blue transition-all duration-300 whitespace-nowrap rounded-lg hover:bg-clinic-blue/5"
            >
              Contact
            </Link>
            <Link
              to="/jeux"
              className="underline-expand px-2 lg:px-3 py-2 text-xs lg:text-sm font-medium text-clinic-blue-dark hover:text-clinic-blue transition-all duration-300 whitespace-nowrap rounded-lg hover:bg-clinic-blue/5"
            >
              Jeux
            </Link>
          </nav>

          {/* === ESPACE DROITE : Utilisateur connecté ou boutons connexion === */}
          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
            {user ? (
              <>
                {/* Cloche de notifications */}
                <div className="relative">
                  <NotificationBell />
                  {showPasswordWarning && (
                    <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse border-2 border-white"></div>
                  )}
                </div>

                {/* Dropdown utilisateur */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                    className="flex items-center gap-2 px-2 md:px-3 py-1.5 rounded-full border border-gray-200 hover:border-clinic-blue/40 hover:bg-gray-50 transition-all cursor-pointer"
                  >
                    {/* Avatar avec initiale */}
                    <div className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-gradient-to-br from-clinic-blue to-clinic-green flex items-center justify-center text-white text-xs md:text-sm font-bold flex-shrink-0">
                      {getUserInitials().toUpperCase()}
                    </div>
                    <span className="hidden lg:inline text-xs xl:text-sm font-medium text-gray-700 max-w-[80px] xl:max-w-[120px] truncate">
                      {user.user_metadata?.prenom || user.email?.split('@')[0] || 'Utilisateur'}
                    </span>
                    <i className={`ri-arrow-down-s-line text-gray-400 text-sm transition-transform duration-200 hidden sm:inline ${userDropdownOpen ? 'rotate-180' : ''}`}></i>
                  </button>

                  {/* Panneau dropdown */}
                  {userDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl border border-gray-200 shadow-lg py-1.5 z-50 animate-fadeIn">
                      {/* En-tête */}
                      <div className="px-4 py-2.5 border-b border-gray-100">
                        <p className="text-sm font-semibold text-clinic-blue-dark truncate">
                          {user.user_metadata?.prenom} {user.user_metadata?.nom || ''}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{user.email}</p>
                      </div>

                      {/* Mon profil */}
                      <Link
                        to="/mon-profil"
                        onClick={() => setUserDropdownOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                      >
                        <i className="ri-user-settings-line text-clinic-blue w-5 text-center"></i>
                        Mon profil
                      </Link>

                      {/* Liens selon le rôle */}
                      {(role === 'super_admin' || role === 'admin') && (
                        <Link
                          to="/admin"
                          onClick={() => setUserDropdownOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <i className="ri-admin-line text-clinic-blue w-5 text-center"></i>
                          Administration
                        </Link>
                      )}
                      {role === 'secretaire' && (
                        <Link
                          to="/secretaire/dashboard"
                          onClick={() => setUserDropdownOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <i className="ri-dashboard-line text-clinic-blue w-5 text-center"></i>
                          Secrétariat
                        </Link>
                      )}
                      {userRoles.includes('medecin') && (
                        <>
                          <Link
                            to="/medecin/rendez-vous"
                            onClick={() => setUserDropdownOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                          >
                            <i className="ri-calendar-check-line text-clinic-blue w-5 text-center"></i>
                            Mes rendez-vous
                          </Link>
                          <Link
                            to="/medecin/disponibilites"
                            onClick={() => setUserDropdownOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                          >
                            <i className="ri-time-line text-clinic-blue w-5 text-center"></i>
                            Mes disponibilités
                          </Link>
                          <Link
                            to="/medecin/guide"
                            onClick={() => setUserDropdownOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                          >
                            <i className="ri-book-open-line text-clinic-blue w-5 text-center"></i>
                            Guide du médecin
                          </Link>
                        </>
                      )}
                      {userRoles.includes('patient') && (
                        <Link
                          to="/patient/rendez-vous"
                          onClick={() => setUserDropdownOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <i className="ri-calendar-check-line text-clinic-blue w-5 text-center"></i>
                          Mes rendez-vous
                        </Link>
                      )}
                      {userRoles.includes('laborantin') && (
                        <Link
                          to="/laborantin/dashboard"
                          onClick={() => setUserDropdownOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <i className="ri-flask-line text-clinic-blue w-5 text-center"></i>
                          Espace Biomédical
                        </Link>
                      )}
                      {userRoles.includes('sage_femme') && (
                        <>
                          <Link
                            to="/sage-femme/dashboard"
                            onClick={() => setUserDropdownOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                          >
                            <i className="ri-nurse-line text-clinic-blue w-5 text-center"></i>
                            Espace sage-femme
                          </Link>
                          <Link
                            to="/medecin/recherche-patients"
                            onClick={() => setUserDropdownOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                          >
                            <i className="ri-user-search-line text-clinic-blue w-5 text-center"></i>
                            Dossiers patients
                          </Link>
                        </>
                      )}
                      {userRoles.includes('infirmier') && (
                        <Link
                          to="/infirmier/dashboard"
                          onClick={() => setUserDropdownOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <i className="ri-empathize-line text-clinic-blue w-5 text-center"></i>
                          Espace infirmier
                        </Link>
                      )}

                      {/* Visioconférence - pour tous les non-patients */}
                      {!userRoles.includes('patient') && (
                        <Link
                          to="/visioconference"
                          onClick={() => setUserDropdownOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <i className="ri-vidicon-line text-clinic-blue w-5 text-center"></i>
                          Visioconférence
                        </Link>
                      )}



                      {/* Séparateur + Déconnexion */}
                      <div className="border-t border-gray-100 mt-1 pt-1">
                        <button
                          onClick={() => {
                            setUserDropdownOpen(false);
                            handleSignOut();
                          }}
                          className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                        >
                          <i className="ri-logout-box-r-line w-5 text-center"></i>
                          Déconnexion
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="hidden sm:inline-flex px-3 lg:px-5 py-2 border-2 border-clinic-blue text-clinic-blue rounded-full hover:bg-clinic-blue hover:text-white transition-all text-xs lg:text-sm font-medium whitespace-nowrap cursor-pointer"
                >
                  Connexion
                </Link>
                <Link
                  to="/rendez-vous"
                  className="btn-accent px-3 lg:px-5 py-2 rounded-full text-xs lg:text-sm font-medium whitespace-nowrap cursor-pointer"
                >
                  Prendre RDV
                </Link>
              </>
            )}

            {/* Burger mobile */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-1.5 text-clinic-blue-dark hover:text-clinic-green transition-colors cursor-pointer"
              aria-label="Menu"
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* === MOBILE MENU === */}
        {mobileMenuOpen && (
          <nav className="md:hidden py-4 border-t border-gray-100 animate-fadeIn">
            <div className="flex flex-col gap-1.5">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-clinic-blue-dark hover:text-clinic-blue hover:bg-clinic-blue/5 rounded-xl transition-all text-sm font-medium"
              >
                <i className="ri-home-line w-5 text-center text-clinic-blue"></i>
                Accueil
              </Link>
              <Link
                to="/services"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-clinic-blue-dark hover:text-clinic-blue hover:bg-clinic-blue/5 rounded-xl transition-all text-sm font-medium"
              >
                <i className="ri-service-line w-5 text-center text-clinic-blue"></i>
                Nos services
              </Link>
              <Link
                to="/actualites"
                onClick={() => setMobileMenuOpen(false)}
                className={
                  animationActive
                    ? 'actu-btn relative flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium'
                    : 'flex items-center gap-3 px-4 py-3 text-clinic-blue-dark hover:text-clinic-blue hover:bg-clinic-blue/5 rounded-xl transition-all text-sm font-medium'
                }
              >
                <i className={animationActive ? 'ri-megaphone-line text-base' : 'ri-megaphone-line w-5 text-center text-clinic-blue'}></i>
                Actualités
              </Link>
              <Link
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-clinic-blue-dark hover:text-clinic-blue hover:bg-clinic-blue/5 rounded-xl transition-all text-sm font-medium"
              >
                <i className="ri-mail-line w-5 text-center text-clinic-blue"></i>
                Contact
              </Link>
              <Link
                to="/jeux"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-clinic-blue-dark hover:text-clinic-blue hover:bg-clinic-blue/5 rounded-xl transition-all text-sm font-medium"
              >
                <i className="ri-gamepad-line w-5 text-center text-clinic-blue"></i>
                Jeux éducatifs
              </Link>

              <div className="border-t border-gray-100 my-1"></div>

              {user ? (
                <>

                  <button
                    onClick={() => {
                      handleSignOut();
                      setMobileMenuOpen(false);
                    }}
                    className="flex items-center gap-3 px-4 py-3 text-red-500 hover:bg-red-50 rounded-xl transition-all text-sm font-medium cursor-pointer text-left w-full"
                  >
                    <i className="ri-logout-box-r-line w-5 text-center"></i>
                    Déconnexion
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-3 border-2 border-clinic-blue text-clinic-blue rounded-xl hover:bg-clinic-blue hover:text-white transition-all text-sm font-medium text-center"
                  >
                    Connexion
                  </Link>
                  <Link
                    to="/rendez-vous"
                    onClick={() => setMobileMenuOpen(false)}
                    className="btn-accent px-4 py-3 rounded-xl text-sm font-medium text-center"
                  >
                    Prendre RDV
                  </Link>
                </>
              )}
            </div>
          </nav>
        )}
      </div>
    </header>
    {/* Rappel des actualités (icône flottante + pop-up) pour les visiteurs non connectés */}
    <PublicActualiteNotifier />
    </>
  );
}